/**
 * Date Utility Functions
 * @description Utility functions for date and time formatting
 */

/**
 * Converts a date string to YYYY-MM-DD format for API use (e.g. 2026-02-24).
 * Accepts ISO strings, date-only strings, or any parseable date.
 * @param dateStr - Date string (e.g. ISO "2026-02-24T00:00:00.000Z" or "2026-02-24")
 * @returns Date in YYYY-MM-DD format, or empty string if invalid/missing
 */
export const toDateOnly = (dateStr: string | undefined): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

/**
 * Formats a duration from an ISO date string to a human-readable format
 * @param fromIso - ISO date string (e.g., "2025-11-25T05:43:33.807610")
 * @returns Formatted duration string (e.g., "5d", "12h", "30min", "1min")
 */
export const formatDurationDHHMM = (fromIso?: string): string => {
  if (!fromIso) return '1min';
  const from = new Date(fromIso).getTime();
  const now = Date.now();
  const diffMs = Math.max(0, now - from);
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  
  const totalMinutes = Math.floor(diffMs / minuteMs);
  const totalHours = Math.floor(diffMs / hourMs);
  const totalDays = Math.floor(diffMs / dayMs);
  
  // If >= 24 hours (1 day), show in days format
  if (totalDays >= 1) {
    return `${totalDays}d`;
  }
  
  // If >= 1 hour but < 24 hours, show in hours format
  if (totalHours >= 1) {
    return `${totalHours}h`;
  }
  
  // If < 1 hour, show in minutes format (minimum 1min)
  return `${Math.max(1, totalMinutes)}min`;
};

/**
 * Calculates duration between two dates and formats it as a human-readable string
 * @param startDate - ISO date string for the start date (e.g., "2025-11-25T05:43:33.807610")
 * @param endDate - ISO date string for the end date, or null for ongoing incidents (uses current time)
 * @returns Formatted duration string (e.g., "5d", "12h", "30min", "1min") or "-" if startDate is invalid
 */
export const calculateDuration = (startDate: string | null, endDate: string | null): string => {
  if (!startDate) return '-';
  
  const start = new Date(startDate).getTime();
  const end = endDate ? new Date(endDate).getTime() : Date.now();
  
  // Validate dates
  if (isNaN(start) || isNaN(end)) return '-';
  
  const diffMs = Math.max(0, end - start);
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  
  const totalMinutes = Math.floor(diffMs / minuteMs);
  const totalHours = Math.floor(diffMs / hourMs);
  const totalDays = Math.floor(diffMs / dayMs);
  
  // If >= 24 hours (1 day), show in days format
  if (totalDays >= 1) {
    return `${totalDays}d`;
  }
  
  // If >= 1 hour but < 24 hours, show in hours format
  if (totalHours >= 1) {
    return `${totalHours}h`;
  }
  
  // If < 1 hour, show in minutes format (minimum 1min)
  return `${Math.max(1, totalMinutes)}min`;
};

/**
 * Formats a date string (treats input as UTC, formats to local timezone)
 * @param dateString - ISO date string (e.g., "2025-11-25T05:43:33.807610" or "2025-11-25T05:43:33Z")
 * @param includeSeconds - Optional flag to include seconds in the formatted output (default: false)
 * @returns Formatted date string (e.g., "Nov 25, 2025, 10:43 AM") or "-" if dateString is null/empty
 */
export const formatDate = (dateString: string | null, includeSeconds: boolean = false): string => {
  if (!dateString) return '-';
  
  // Ensure the date string is treated as UTC
  // Remove any existing timezone offset and append 'Z' to make it explicitly UTC
  let utcDateString = dateString?.trim();
  
  // If it already ends with 'Z', it's already UTC
  if (utcDateString.endsWith('Z')) {
    // Already UTC, use as is
  } else {
    // Remove timezone offset if present (e.g., '+05:00', '-05:00', '+0500', '-0500')
    utcDateString = utcDateString.replace(/[+-]\d{2}:?\d{2}$/, '');
    // Append 'Z' to make it explicitly UTC
    utcDateString = `${utcDateString}Z`;
  }
  
  // Parse as UTC and format to local timezone
  const date = new Date(utcDateString);
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  
  if (includeSeconds) {
    options.second = '2-digit';
  }
  
  return date.toLocaleString('en-US', options);
};

/**
 * Converts a date to a specific timezone
 * @param date - Date object or date string to convert
 * @param timeZone - IANA timezone string (e.g., "America/New_York", "Asia/Calcutta", "UTC")
 * @returns Date object representing the same moment in time, formatted for the target timezone
 * @description This function converts a date to display in a specific timezone.
 * The returned Date object represents the same moment in time but can be formatted
 * to show the time in the target timezone.
 */
export const convertToTimezone = (date: Date | string, timeZone: string): Date => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    throw new Error('Invalid date provided to convertToTimezone');
  }

  // Format the date in the target timezone
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(dateObj);

  // Parse the formatted string back to a Date object
  // Format: "MM/DD/YYYY, HH:MM:SS"
  const [datePart, timePart] = formatted?.split(', ');
  const [month, day, year] = datePart?.split('/');
  const [hour, minute, second] = timePart?.split(':');
  
  // Create a new Date in UTC that represents the same local time values
  // This creates a Date that, when formatted in the target timezone, shows the same values
  return new Date(
    Date.UTC(
      parseInt(year, 10),
      parseInt(month, 10) - 1, // Month is 0-indexed
      parseInt(day, 10),
      parseInt(hour, 10),
      parseInt(minute, 10),
      parseInt(second, 10)
    )
  );
};

/**
 * Converts a date from a specific timezone to UTC
 * @param date - Date object or date string (assumed to be in the source timezone)
 * @param timeZone - IANA timezone string (e.g., "America/New_York", "Asia/Calcutta", "UTC")
 * @returns Date object in UTC representing the same moment in time
 * @description This function converts a date that is interpreted as being in a specific
 * timezone to UTC. Useful when you have a date/time that was entered in a specific
 * timezone and need to convert it to UTC for storage or API calls.
 * 
 * Example: If you have "2025-01-01 12:00" in "America/New_York", this converts it to
 * the equivalent UTC time.
 */
export const convertFromTimezone = (date: Date | string, timeZone: string): Date => {
  if (timeZone === 'UTC') {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      throw new Error('Invalid date provided to convertFromTimezone');
    }
    return dateObj;
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    throw new Error('Invalid date provided to convertFromTimezone');
  }

  // Get the date components as they appear in the source timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(dateObj);
  const year = parseInt(parts.find(p => p.type === 'year')?.value || '0', 10);
  const month = parseInt(parts.find(p => p.type === 'month')?.value || '0', 10) - 1; // 0-indexed
  const day = parseInt(parts.find(p => p.type === 'day')?.value || '0', 10);
  const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
  const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
  const second = parseInt(parts.find(p => p.type === 'second')?.value || '0', 10);

  // Create a date string in ISO format (without timezone)
  const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
  
  // Create a temporary date object (this will be interpreted in local timezone)
  const tempDate = new Date(dateString);
  
  // Get what this date would be in UTC vs in the target timezone
  // The difference tells us the offset
  const utcTime = tempDate.getTime();
  const utcDateStr = tempDate.toLocaleString('en-US', { timeZone: 'UTC', hour12: false });
  const tzDateStr = tempDate.toLocaleString('en-US', { timeZone, hour12: false });
  
  // Parse both to get the actual time difference
  const utcParsed = new Date(utcDateStr);
  const tzParsed = new Date(tzDateStr);
  const offset = utcParsed.getTime() - tzParsed.getTime();
  
  // Adjust the original date by the offset to get UTC
  return new Date(utcTime - offset);
};

/**
 * Formats a date in a specific timezone
 * @param date - Date object or date string to format
 * @param timeZone - IANA timezone string (e.g., "America/New_York", "Asia/Calcutta", "UTC")
 * @param options - Optional Intl.DateTimeFormatOptions for custom formatting
 * @returns Formatted date string in the target timezone
 */
export const formatDateInTimezone = (
  date: Date | string,
  timeZone: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '-';
  }

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
    ...options,
  };

  return dateObj.toLocaleString('en-US', defaultOptions);
};

/**
 * Formats a date with timezone awareness - shows only time if same day, otherwise shows date and time
 * @param dateString - ISO date string to format (e.g., "2025-11-25T05:43:33.807610")
 * @param timeZone - IANA timezone string (e.g., "America/New_York", "Asia/Calcutta", "UTC")
 * @param fallbackValue - Value to return if date is invalid (default: 'N/A')
 * @returns Formatted date string - time only if same day (e.g., "14:30:45"), or date and time if different day (e.g., "Nov 25, 2025, 14:30:45")
 */
export const formatDateWithTimezoneAwareness = (
  dateString: string | null | undefined,
  timeZone: string,
  fallbackValue: string = 'N/A'
): string => {
  if (!dateString) return fallbackValue;
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return fallbackValue;
    
    // Get today's date components in the target timezone (date only, no time)
    const today = new Date();
    const todayFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const todayParts = todayFormatter.formatToParts(today);
    const todayYear = todayParts.find(p => p.type === 'year')?.value;
    const todayMonth = todayParts.find(p => p.type === 'month')?.value;
    const todayDay = todayParts.find(p => p.type === 'day')?.value;
    
    // Get the input date components in the target timezone (date only, no time)
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const dateParts = dateFormatter.formatToParts(date);
    const dateYear = dateParts.find(p => p.type === 'year')?.value;
    const dateMonth = dateParts.find(p => p.type === 'month')?.value;
    const dateDay = dateParts.find(p => p.type === 'day')?.value;
    
    // Check if it's the same day by comparing only date components (year, month, day)
    const isSameDay = todayYear === dateYear && 
                      todayMonth === dateMonth && 
                      todayDay === dateDay;
    
    if (isSameDay) {
      // Show only time with AM/PM (no date)
      const timeFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
      return timeFormatter.format(date);
    } else {
      // Show date and time with AM/PM
      return formatDateInTimezone(date, timeZone, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }
  } catch {
    return fallbackValue;
  }
};

/**
 * Formats uptime duration string with conditional display rules
 * @param durationFormatted - Formatted duration string from API (e.g., "5d 12h 30m 15s", "1h 30m", "45m 30s", "30s")
 * @returns Formatted duration string:
 *   - If days available: "Xd Xh" (day and hour only, e.g., "5d 12h")
 *   - If no days but hours available: "Xh Xm" (hour and minute, e.g., "12h 30m")
 *   - If no days/hours: "Xm Xs" (minute and second, e.g., "45m 30s")
 *   - Returns "-" if input is null/empty/invalid format
 * @example
 * formatUptimeDuration("5d 12h 30m 15s") // "5d 12h"
 * formatUptimeDuration("12h 30m 15s") // "12h 30m"
 * formatUptimeDuration("45m 30s") // "45m 30s"
 * formatUptimeDuration("30s") // "0m 30s"
 * formatUptimeDuration(null) // "-"
 */
export const formatUptimeDuration = (durationFormatted: string | null | undefined): string => {
  if (!durationFormatted || durationFormatted === '-') return '-';
  
  // Parse the duration string to extract days, hours, minutes, seconds
  // Format examples: "5d 12h 30m 15s", "1h 30m", "45m 30s", "30s"
  const dayMatch = durationFormatted.match(/(\d+)d/);
  const hourMatch = durationFormatted.match(/(\d+)h/);
  const minuteMatch = durationFormatted.match(/(\d+)m/);
  const secondMatch = durationFormatted.match(/(\d+)s/);
  
  const days = dayMatch ? parseInt(dayMatch[1], 10) : 0;
  const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
  const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;
  const seconds = secondMatch ? parseInt(secondMatch[1], 10) : 0;
  
  // Check if we found any valid duration components (if not, it's invalid format)
  const hasValidFormat = dayMatch || hourMatch || minuteMatch || secondMatch;
  if (!hasValidFormat) {
    return '-';
  }
  
  // If days are available: show only day and hour
  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  
  // If day is not available but hours are: show hour and minute
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  
  // If hour is not available: show minute and seconds (including zero values)
  if (minutes >= 0 && seconds >= 0) {
    return `${minutes}m ${seconds}s`;
  }
  
  // Fallback for invalid format
  return '-';
};

/**
 * Formats a date as a short date string (e.g., "Jan 27, 2026")
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string (e.g., "Jan 27, 2026") or "N/A" if invalid
 */
export const formatDateShort = (dateString: string | Date): string => {
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Formats a date as a short date string without year (e.g., "Jan 27")
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string (e.g., "Jan 27") or "N/A" if invalid
 */
export const formatDateShortWithoutYear = (dateString: string | Date): string => {
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Formats a date range with smart formatting:
 * - If end date is at midnight (00:00:00) UTC, considers it as previous day
 * - If same year, shows year only on second date (e.g., "Jan 27 - Feb 3, 2026")
 * - If different years, shows year on both dates (e.g., "Dec 27, 2025 - Jan 3, 2026")
 * @param startDate - ISO date string for start date
 * @param endDate - ISO date string for end date
 * @returns Formatted date range string or "N/A" if invalid
 */
export const formatDateRangeShort = (startDate: string, endDate: string): string => {
  try {
    const start = new Date(startDate);
    let end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'N/A';
    }
    
    // Check if end date is at midnight (00:00:00) UTC
    // If so, subtract one day to show the previous day as the end date
    const endUTCHours = end.getUTCHours();
    const endUTCMinutes = end.getUTCMinutes();
    const endUTCSeconds = end.getUTCSeconds();
    const endUTCMilliseconds = end.getUTCMilliseconds();
    
    if (endUTCHours === 0 && endUTCMinutes === 0 && endUTCSeconds === 0 && endUTCMilliseconds === 0) {
      // End date is at midnight UTC, subtract one day
      end = new Date(end.getTime() - 24 * 60 * 60 * 1000);
    }
    
    // Check if both dates are in the same year
    const isSameYear = start.getFullYear() === end.getFullYear();
    
    if (isSameYear) {
      // Same year: "Jan 27 - Feb 3, 2026"
      const startFormatted = formatDateShortWithoutYear(startDate);
      const endFormatted = formatDateShort(end.toISOString());
      return `${startFormatted} - ${endFormatted}`;
    } else {
      // Different years: "Dec 27, 2025 - Jan 3, 2026"
      const startFormatted = formatDateShort(startDate);
      const endFormatted = formatDateShort(end.toISOString());
      return `${startFormatted} - ${endFormatted}`;
    }
  } catch {
    return 'N/A';
  }
};

/**
 * Formats a period display (single date or date range) with smart formatting:
 * - If end date is at midnight (00:00:00) UTC, considers it as previous day
 * - If same day after adjustment, returns single date format (e.g., "Jan 29, 2026")
 * - If different days, returns date range format with same year optimization
 * @param startDate - ISO date string for start date
 * @param endDate - ISO date string for end date
 * @returns Formatted date string, date range string, or null if invalid/missing dates
 */
export const formatPeriodDisplay = (startDate: string | null | undefined, endDate: string | null | undefined): string | null => {
  if (!startDate || !endDate) return null;
  
  try {
    const start = new Date(startDate);
    let end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return null;
    }
    
    // Check if end date is at midnight (00:00:00) UTC
    // If so, subtract one day to show the previous day as the end date
    const endUTCHours = end.getUTCHours();
    const endUTCMinutes = end.getUTCMinutes();
    const endUTCSeconds = end.getUTCSeconds();
    const endUTCMilliseconds = end.getUTCMilliseconds();
    
    if (endUTCHours === 0 && endUTCMinutes === 0 && endUTCSeconds === 0 && endUTCMilliseconds === 0) {
      // End date is at midnight UTC, subtract one day
      end = new Date(end.getTime() - 24 * 60 * 60 * 1000);
    }
    
    // Check if it's the same day (after adjustment)
    const isSameDay = start.toDateString() === end.toDateString();
    
    if (isSameDay) {
      // Single date: format as "Jan 29, 2026"
      return formatDateShort(startDate);
    } else {
      // Date range: use utility function that handles same year formatting
      return formatDateRangeShort(startDate, endDate);
    }
  } catch (e) {
    console.error('Failed to format period dates:', e);
    return null;
  }
};

